import { WebDashboardContentSpecBlocksInner } from "@coeff/agent-types";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import {
  $createParagraphNode,
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $isElementNode,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  KEY_ENTER_COMMAND,
  LexicalNode,
  LexicalEditor,
  PASTE_COMMAND,
} from "lexical";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";

import { $createBlockMentionNode, $isBlockMentionNode, BlockMentionNode } from "./BlockMentionNode";
import { splitThemeRefs } from "./themeChipRef";
import { $createThemeMentionNode, ThemeMentionNode } from "./ThemeMentionNode";

export interface ChatLexicalEditorHandle {
  insertBlockChip: (blockId: string, blockTitle: string, blockType: string) => void;
  replaceMentionWithChip: (blockId: string, blockTitle: string, blockType: string) => void;
  getBlockMentions: () => Array<{ blockId: string; blockTitle: string; blockType: string }>;
  focus: () => void;
  setText: (text: string) => void;
}

export interface MentionState {
  active: boolean;
  query: string;
  nodeKey?: string;
  atOffset?: number;
  cursorOffset?: number;
}

export interface ChatLexicalEditorProps {
  disabled?: boolean;
  isOpen: boolean;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onMentionStateChange: (state: MentionState) => void;
  onBlockContextsChange?: (
    contexts: Array<{ blockId: string; blockTitle: string; blockType: string }>
  ) => void;
  onBlockSelected?: (blockId: string, blockTitle: string, blockType: string) => void;
  availableBlocks?: WebDashboardContentSpecBlocksInner[];
  onWillChangeHeight?: () => boolean;
  onDidChangeHeight?: (wasAtBottom: boolean) => void;
  /** Ref to whether mention dropdown is active (prevents Enter from submitting) */
  mentionActiveRef: React.MutableRefObject<boolean>;
  /** Ref to saved mention anchor for restoring after blur */
  savedMentionRef: React.MutableRefObject<{
    nodeKey: string;
    atOffset: number;
    cursorOffset: number;
  } | null>;
  blurTimeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
}

const CHAT_INPUT_THEME = {
  root: "chat-input-root",
  paragraph: "chat-input-paragraph",
};

const LexicalErrorBoundary: React.FC<{
  children: React.ReactElement;
  onError: (error: Error) => void;
}> = ({ children }) => <>{children}</>;

const escapeForBlockRef = (str: string) => str.replace(/\\/g, "\\\\").replace(/"/g, '\\"');

// Same tag format serializeEditorState writes and MessageComponent's badge renderer parses.
const BLOCK_REF_PATTERN = /\[block id="((?:[^"\\]|\\.)*)" title="((?:[^"\\]|\\.)*)"\]/g;
const unescapeBlockRef = (str: string): string => str.replace(/\\"/g, '"').replace(/\\\\/g, "\\");

/**
 * Splits prefill text on [block id ...] tags, turning each into a mention chip so the
 * composer shows the block like a hand-inserted @mention instead of the raw tag. Block type
 * defaults to "chart": the tag doesn't carry a type, the only prefill embedding one today is
 * the chart AI-convert draft, and the type only picks the chip's icon.
 */
function $blockRefsToNodes(text: string): LexicalNode[] {
  const nodes: LexicalNode[] = [];
  let last = 0;
  BLOCK_REF_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = BLOCK_REF_PATTERN.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push($createTextNode(text.slice(last, match.index)));
    }
    nodes.push(
      $createBlockMentionNode(unescapeBlockRef(match[1]), unescapeBlockRef(match[2]), "chart")
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    nodes.push($createTextNode(text.slice(last)));
  }
  return nodes;
}

function serializeEditorState(editor: LexicalEditor): string {
  let text = "";
  editor.getEditorState().read(() => {
    const root = $getRoot();
    const paragraphs = root.getChildren();
    paragraphs.forEach((paragraph, pIdx) => {
      if (pIdx > 0) {
        text += "\n";
      }
      if ($isElementNode(paragraph)) {
        paragraph.getChildren().forEach(child => {
          if ($isBlockMentionNode(child)) {
            text += `[block id="${escapeForBlockRef(child.__blockId)}" title="${escapeForBlockRef(
              child.__blockTitle
            )}"]`;
          } else {
            text += child.getTextContent();
          }
        });
      } else {
        text += paragraph.getTextContent();
      }
    });
  });
  return text;
}

function extractBlockMentions(
  editor: LexicalEditor
): Array<{ blockId: string; blockTitle: string; blockType: string }> {
  const mentions: Array<{ blockId: string; blockTitle: string; blockType: string }> = [];
  editor.getEditorState().read(() => {
    const root = $getRoot();
    const walk = (node: ReturnType<typeof $getRoot>) => {
      for (const child of node.getChildren()) {
        if ($isBlockMentionNode(child)) {
          mentions.push({
            blockId: child.__blockId,
            blockTitle: child.__blockTitle,
            blockType: child.__blockType,
          });
        }
        if ("getChildren" in child && typeof child.getChildren === "function") {
          walk(child as ReturnType<typeof $getRoot>);
        }
      }
    };
    walk(root);
  });
  return mentions;
}

// --- Lexical Plugins ---

const EditorRefPlugin: React.FC<{
  editorRef: React.MutableRefObject<LexicalEditor | null>;
}> = ({ editorRef }) => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editorRef.current = editor;
    return () => {
      editorRef.current = null;
    };
  }, [editor, editorRef]);
  return null;
};

const SubmitPlugin: React.FC<{
  onSubmit: (e: React.FormEvent) => void;
  isDisabled: boolean;
  mentionActiveRef: React.MutableRefObject<boolean>;
}> = ({ onSubmit, isDisabled, mentionActiveRef }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event: KeyboardEvent | null) => {
        if (!event) {
          return false;
        }
        if (mentionActiveRef.current) {
          event.preventDefault();
          return true;
        }
        if (event.shiftKey) {
          return false;
        }
        event.preventDefault();
        if (!isDisabled) {
          const syntheticEvent = new Event("submit") as unknown as React.FormEvent;
          onSubmit(syntheticEvent);
        }
        return true;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, onSubmit, isDisabled, mentionActiveRef]);

  return null;
};

const SyncPlugin: React.FC<{
  onChange: (value: string) => void;
  onBlockContextsChange?: (
    contexts: Array<{ blockId: string; blockTitle: string; blockType: string }>
  ) => void;
  onMentionStateChange: (state: MentionState) => void;
  availableBlocks?: WebDashboardContentSpecBlocksInner[];
  onWillChangeHeight?: () => boolean;
  onDidChangeHeight?: (wasAtBottom: boolean) => void;
}> = ({
  onChange,
  onBlockContextsChange,
  onMentionStateChange,
  availableBlocks,
  onWillChangeHeight,
  onDidChangeHeight,
}) => {
  const [editor] = useLexicalComposerContext();
  const lastHeightRef = useRef<number>(0);
  const lastTextRef = useRef<string | null>(null);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      const text = serializeEditorState(editor);
      const didTextChange = text !== lastTextRef.current;
      if (didTextChange) {
        lastTextRef.current = text;
        onChange(text);
      }

      if (onBlockContextsChange) {
        const mentions = extractBlockMentions(editor);
        onBlockContextsChange(mentions);
      }

      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !availableBlocks?.length) {
          onMentionStateChange({ active: false, query: "" });
          return;
        }

        const anchor = selection.anchor;
        if (anchor.type !== "text") {
          onMentionStateChange({ active: false, query: "" });
          return;
        }

        const anchorNode = anchor.getNode();
        if (!$isTextNode(anchorNode)) {
          onMentionStateChange({ active: false, query: "" });
          return;
        }

        const textContent = anchorNode.getTextContent();
        const cursorOffset = anchor.offset;

        let atIndex = -1;
        for (let i = cursorOffset - 1; i >= 0; i--) {
          const ch = textContent[i];
          if (ch === "@") {
            if (i === 0 || /\s/.test(textContent[i - 1])) {
              atIndex = i;
            }
            break;
          }
          if (ch === " " || ch === "\n") {
            break;
          }
        }

        if (atIndex >= 0) {
          const query = textContent.slice(atIndex + 1, cursorOffset);
          onMentionStateChange({
            active: true,
            query,
            nodeKey: anchorNode.getKey(),
            atOffset: atIndex,
            cursorOffset,
          });
        } else {
          onMentionStateChange({ active: false, query: "" });
        }
      });

      // The content's rendered height can only change when the text changes — never on a
      // selection-only update (click, arrow keys, Cmd+↑/↓). Re-measuring on those is what
      // broke scrolling: the `height: "auto"` probe below momentarily removes the overflow,
      // so the browser resets scrollTop to 0, and since the caret-into-view fixup only ran on
      // text changes, the view stayed pinned to the top (clicks jumped up; Cmd+↓ looked dead).
      // Skipping the whole block on selection-only updates lets the browser handle caret
      // visibility natively.
      if (!didTextChange) {
        return;
      }

      requestAnimationFrame(() => {
        const rootEl = editor.getRootElement();
        if (!rootEl) {
          return;
        }
        const lineHeight = 24;
        const minHeight = 56;
        const maxHeight = 200;

        // Temporarily reset height to measure true content height. Setting height to "auto"
        // removes the overflow and forces scrollTop to 0, so snapshot and restore it.
        const prevScrollTop = rootEl.scrollTop;
        const prevHeight = rootEl.style.height;
        rootEl.style.height = "auto";
        const contentHeight = rootEl.scrollHeight;
        rootEl.style.height = prevHeight;
        rootEl.scrollTop = prevScrollTop;

        const newHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
        const isOverflowing = contentHeight > maxHeight;

        if (lastHeightRef.current !== newHeight) {
          const wasAtBottom = onWillChangeHeight?.() ?? false;
          rootEl.style.height = `${newHeight}px`;
          rootEl.style.overflowY = isOverflowing ? "auto" : "hidden";
          lastHeightRef.current = newHeight;
          if (onDidChangeHeight) {
            setTimeout(() => onDidChangeHeight(wasAtBottom), 200);
          }
        }

        // Keep the caret visible while typing into an overflowing box.
        if (isOverflowing) {
          const domSelection = rootEl.ownerDocument.getSelection();
          if (domSelection && domSelection.rangeCount > 0) {
            const range = domSelection.getRangeAt(0);
            const cursorRect = range.getBoundingClientRect();
            // A collapsed selection at a node boundary can report an empty rect at (0, 0);
            // ignore it so we don't spuriously scroll back to the top.
            if (cursorRect.height > 0 || cursorRect.top > 0) {
              const rootRect = rootEl.getBoundingClientRect();
              if (cursorRect.bottom > rootRect.bottom) {
                rootEl.scrollTop += cursorRect.bottom - rootRect.bottom + lineHeight;
              } else if (cursorRect.top < rootRect.top) {
                rootEl.scrollTop -= rootRect.top - cursorRect.top + lineHeight;
              }
            }
          }
        }
      });
    });
  }, [
    editor,
    onChange,
    onBlockContextsChange,
    onMentionStateChange,
    availableBlocks,
    onWillChangeHeight,
    onDidChangeHeight,
  ]);

  return null;
};

const AutoFocusOnOpenPlugin: React.FC<{ isOpen: boolean; isDisabled: boolean }> = ({
  isOpen,
  isDisabled,
}) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (isOpen && !isDisabled) {
      editor.focus();
    }
  }, [editor, isOpen, isDisabled]);

  return null;
};

/** Chip node types the paste path has to rebuild by hand (see rebuildNodesFromSerialized). */
const CHIP_NODE_TYPES = ["block-mention", "theme-mention"];

export function hasSerializedChips(nodes: unknown[]): boolean {
  for (const node of nodes) {
    const n = node as Record<string, unknown>;
    if (typeof n.type === "string" && CHIP_NODE_TYPES.includes(n.type)) {
      return true;
    }
    if (Array.isArray(n.children) && hasSerializedChips(n.children)) {
      return true;
    }
  }
  return false;
}

export function rebuildNodesFromSerialized(nodes: unknown[]): LexicalNode[] {
  const result: LexicalNode[] = [];
  for (const node of nodes) {
    const n = node as Record<string, unknown>;
    if (n.type === "block-mention") {
      result.push(
        $createBlockMentionNode(n.blockId as string, n.blockTitle as string, n.blockType as string)
      );
    } else if (n.type === "theme-mention") {
      // Dropping this would strip the theme id the edit flow rides on, leaving a sentence
      // ("Update my  to be darker") the agent can't act on.
      result.push(
        $createThemeMentionNode(
          n.themeId as string,
          Array.isArray(n.colors) ? (n.colors as string[]) : []
        )
      );
    } else if (n.type === "text") {
      result.push($createTextNode((n.text as string) || ""));
    } else if (n.type === "linebreak") {
      result.push($createTextNode("\n"));
    } else if (n.type === "paragraph" && Array.isArray(n.children)) {
      if (result.length > 0) {
        result.push($createTextNode("\n"));
      }
      result.push(...rebuildNodesFromSerialized(n.children));
    }
  }
  return result;
}

const PasteChipPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: InputEvent | ClipboardEvent) => {
        const clipboardData = "clipboardData" in event ? event.clipboardData : null;
        if (!clipboardData) {
          return false;
        }

        const lexicalData = clipboardData.getData("application/x-lexical-editor");
        if (!lexicalData) {
          return false;
        }

        try {
          const parsed = JSON.parse(lexicalData);
          if (!Array.isArray(parsed.nodes) || !hasSerializedChips(parsed.nodes)) {
            return false;
          }

          event.preventDefault();
          editor.update(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) {
              return;
            }
            const rebuilt = rebuildNodesFromSerialized(parsed.nodes);
            if (rebuilt.length > 0) {
              selection.insertNodes(rebuilt);
            }
          });

          return true;
        } catch {
          return false;
        }
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);

  return null;
};

const ValueSyncPlugin: React.FC<{ value: string }> = ({ value }) => {
  const [editor] = useLexicalComposerContext();
  const lastValueRef = useRef(value);

  useEffect(() => {
    if (value === "" && lastValueRef.current !== "") {
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        root.append($createParagraphNode());
      });
    }
    lastValueRef.current = value;
  }, [editor, value]);

  return null;
};

interface InsertChipHandle {
  insertBlockChip: (blockId: string, blockTitle: string, blockType: string) => void;
}

const InsertChipPlugin = forwardRef<
  InsertChipHandle,
  {
    onBlockSelected?: (blockId: string, blockTitle: string, blockType: string) => void;
  }
>(({ onBlockSelected }, ref) => {
  const [editor] = useLexicalComposerContext();

  useImperativeHandle(
    ref,
    () => ({
      insertBlockChip(blockId: string, blockTitle: string, blockType: string) {
        editor.update(() => {
          const mentionNode = $createBlockMentionNode(blockId, blockTitle, blockType);
          const trailingSpace = $createTextNode(" ");
          const root = $getRoot();

          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            selection.insertNodes([mentionNode, trailingSpace]);
          } else {
            const lastChild = root.getLastChild();
            if (lastChild && $isElementNode(lastChild)) {
              lastChild.append(mentionNode, trailingSpace);
            } else {
              const p = $createParagraphNode();
              p.append(mentionNode, trailingSpace);
              root.append(p);
            }
          }
          trailingSpace.select();
        });
        onBlockSelected?.(blockId, blockTitle, blockType);
        editor.focus();
      },
    }),
    [editor, onBlockSelected]
  );

  return null;
});

InsertChipPlugin.displayName = "InsertChipPlugin";

// --- Main Component ---

export const ChatLexicalEditor = forwardRef<ChatLexicalEditorHandle, ChatLexicalEditorProps>(
  (
    {
      disabled = false,
      isOpen,
      value,
      placeholder,
      onChange,
      onSubmit,
      onMentionStateChange,
      onBlockContextsChange,
      onBlockSelected,
      availableBlocks,
      onWillChangeHeight,
      onDidChangeHeight,
      mentionActiveRef,
      savedMentionRef,
      blurTimeoutRef,
    },
    ref
  ) => {
    const editorRef = useRef<LexicalEditor | null>(null);
    const insertChipRef = useRef<InsertChipHandle>(null);
    const handleMentionSelectRef = useRef<
      (blockId: string, blockTitle: string, blockType: string) => void
    >(() => {});

    const replaceMentionWithChip = useCallback(
      (blockId: string, blockTitle: string, blockType: string) => {
        const editor = editorRef.current;
        if (!editor) {
          return;
        }

        if (blurTimeoutRef.current !== null) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }

        editor.update(() => {
          let anchorNode: ReturnType<typeof $createTextNode> | null = null;
          let atIndex = -1;
          let cursorOffset = 0;

          const selection = $getSelection();
          if ($isRangeSelection(selection) && selection.anchor.type === "text") {
            const node = selection.anchor.getNode();
            if ($isTextNode(node)) {
              anchorNode = node;
              cursorOffset = selection.anchor.offset;
              const text = node.getTextContent();
              for (let i = cursorOffset - 1; i >= 0; i--) {
                if (text[i] === "@") {
                  atIndex = i;
                  break;
                }
              }
            }
          }

          if (atIndex < 0 && savedMentionRef.current) {
            const { nodeKey, atOffset, cursorOffset: savedCursor } = savedMentionRef.current;
            const savedNode = $getNodeByKey(nodeKey);
            if (savedNode && $isTextNode(savedNode)) {
              anchorNode = savedNode;
              atIndex = atOffset;
              cursorOffset = savedCursor;
            }
          }

          if (atIndex < 0 || !anchorNode) {
            const mentionNode = $createBlockMentionNode(blockId, blockTitle, blockType);
            const trailingSpace = $createTextNode(" ");
            const root = $getRoot();
            const lastChild = root.getLastChild();
            if (lastChild && $isElementNode(lastChild)) {
              lastChild.append(mentionNode, trailingSpace);
            } else {
              const p = $createParagraphNode();
              p.append(mentionNode, trailingSpace);
              root.append(p);
            }
            trailingSpace.select();
            return;
          }

          const textContent = anchorNode.getTextContent();
          const before = textContent.slice(0, atIndex);
          const after = textContent.slice(cursorOffset);

          const mentionNode = $createBlockMentionNode(blockId, blockTitle, blockType);
          const trailingSpace = $createTextNode(" ");

          if (before || after) {
            if (before) {
              anchorNode.setTextContent(before);
              anchorNode.insertAfter(mentionNode);
              mentionNode.insertAfter(trailingSpace);
              if (after) {
                const afterNode = $createTextNode(after);
                trailingSpace.insertAfter(afterNode);
              }
            } else {
              anchorNode.setTextContent(after || "");
              anchorNode.insertBefore(mentionNode);
              mentionNode.insertAfter(trailingSpace);
            }
          } else {
            anchorNode.replace(mentionNode);
            mentionNode.insertAfter(trailingSpace);
          }
          trailingSpace.select();
        });

        savedMentionRef.current = null;
        onBlockSelected?.(blockId, blockTitle, blockType);
        editor.focus();
      },
      [onBlockSelected, savedMentionRef, blurTimeoutRef]
    );

    handleMentionSelectRef.current = replaceMentionWithChip;

    useImperativeHandle(ref, () => ({
      insertBlockChip(blockId: string, blockTitle: string, blockType: string) {
        insertChipRef.current?.insertBlockChip(blockId, blockTitle, blockType);
      },
      replaceMentionWithChip(blockId: string, blockTitle: string, blockType: string) {
        handleMentionSelectRef.current(blockId, blockTitle, blockType);
      },
      getBlockMentions() {
        const editor = editorRef.current;
        if (!editor) {
          return [];
        }
        return extractBlockMentions(editor);
      },
      focus() {
        editorRef.current?.focus();
      },
      setText(text: string) {
        const editor = editorRef.current;
        if (!editor) {
          return;
        }
        editor.update(
          () => {
            const root = $getRoot();
            root.clear();
            const paragraph = $createParagraphNode();
            // Theme references arrive as tokens in the prefill text (see themeChipRef) and become
            // chips, so the composer shows the theme's colors rather than the raw token. Block
            // references (same [block id ...] tag the sent bubbles parse) become chips too — the
            // AI-convert draft prefills one ahead of its prompt text.
            for (const segment of splitThemeRefs(text)) {
              if (segment.themeRef) {
                paragraph.append(
                  $createThemeMentionNode(segment.themeRef.themeId, segment.themeRef.colors)
                );
              } else if (segment.text) {
                for (const node of $blockRefsToNodes(segment.text)) {
                  paragraph.append(node);
                }
              }
            }
            root.append(paragraph);
            // A prefill is a sentence the user finishes typing, so leave the caret at the end
            // (Lexical otherwise starts it at offset 0).
            paragraph.selectEnd();
          },
          { discrete: true }
        );
        editor.focus();
      },
    }));

    const initialConfig = useMemo(
      () => ({
        namespace: "ChatInput",
        nodes: [BlockMentionNode, ThemeMentionNode],
        onError: (error: Error) => console.error("ChatInput Lexical error:", error),
        theme: CHAT_INPUT_THEME,
      }),
      []
    );

    return (
      <LexicalComposer initialConfig={initialConfig}>
        <EditorRefPlugin editorRef={editorRef} />
        <div className="relative">
          <PlainTextPlugin
            contentEditable={
              <ContentEditable
                id="chatInput"
                className="box-border w-full min-h-[64px] resize-none overflow-y-auto px-3 pt-3 pb-1 text-sm text-[#111215] transition-all duration-200 focus:outline-none"
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
                disabled={disabled}
                onBlur={() => {
                  blurTimeoutRef.current = setTimeout(() => {
                    blurTimeoutRef.current = null;
                  }, 200);
                }}
                onFocus={() => {
                  if (blurTimeoutRef.current !== null) {
                    clearTimeout(blurTimeoutRef.current);
                    blurTimeoutRef.current = null;
                  }
                }}
                data-sentry-mask
              />
            }
            placeholder={
              <div className="absolute top-3 left-3 right-4 text-sm text-[#b0b0b0] pointer-events-none select-none truncate">
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>

        <SyncPlugin
          onChange={onChange}
          onBlockContextsChange={onBlockContextsChange}
          onMentionStateChange={onMentionStateChange}
          availableBlocks={availableBlocks}
          onWillChangeHeight={onWillChangeHeight}
          onDidChangeHeight={onDidChangeHeight}
        />
        <SubmitPlugin
          onSubmit={onSubmit}
          isDisabled={disabled}
          mentionActiveRef={mentionActiveRef}
        />
        <PasteChipPlugin />
        <AutoFocusOnOpenPlugin isOpen={isOpen} isDisabled={disabled} />
        <ValueSyncPlugin value={value} />
        <HistoryPlugin />
        <InsertChipPlugin ref={insertChipRef} onBlockSelected={onBlockSelected} />
      </LexicalComposer>
    );
  }
);

ChatLexicalEditor.displayName = "ChatLexicalEditor";
